import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AddLocationAltOutlinedIcon from '@mui/icons-material/AddLocationAltOutlined';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  strapiAddSamplingPointsForWorkOrder,
  strapiBulkSignBeritaAcara,
  strapiCancelSurveyPoint,
  strapiListSurveyTeamsForWorkOrder,
  strapiReorderBeritaAcaraLetters,
  strapiRescheduleProposeSurveyPoint,
  strapiSendWorkOrderBeritaAcaraPdf,
  strapiStartSurvey,
  type StrapiStartSurveyPresenceRow,
  type StrapiSurveyGridRow,
  type SurveyRowStatus,
} from '../../../api/strapiCrm';
import { DataGridNumberedPagination, DATA_GRID_DEFAULT_PAGE_SIZE, DATA_GRID_NUMBERED_PAGE_SIZE_OPTIONS } from '../../../components/DataGridNumberedPagination';
import { useAuth } from '../../../auth/AuthContext';
import { isPetugasSampling } from '../../QuotationsPage/quotationWorkflow';
import { queryKeys } from '../../../query/queryKeys';
import {
  buildInitialTeamPresence,
  type TeamPresenceRowState,
} from '../TabScreens/List/startSurveyTeamPresence';
import { StartSurveyTeamPresenceByDateTabs } from '../TabScreens/List/StartSurveyTeamPresenceByDateTabs';
import {
  applySurveyRowPatch,
  canCancelSurveyRow,
  canEditSurveySamplingRow,
  canFinishSurveyRow,
  canRescheduleSurveyRow,
  canStartSurveyRow,
  collectBlockedRescheduleDates,
  mergeSurveyRowsWithPatches,
  patchSurveyRowAfterSign,
  selectedSignRows,
  surveyRowIsBeritaAcaraPrintReady,
  type SurveyRowPatch,
} from '../surveyRowActions';
import {
  canDragSurveyLetterRow,
  reorderSurveyLetterRows,
  sortSurveyRowsByListOrder,
  surveyRowKodeSampel,
} from '../surveyBeritaAcaraListOrder';
import { readCurrentPosition, readFileAsDataUrl, resolveSurveyStartCoordinates } from '../surveyGeo';
import { SurveyGpsHemisphereResult } from './SurveyGpsHemisphereResult';
import { SurveyBeritaAcaraFinishDialog } from './SurveyBeritaAcaraFinishDialog';
import { SurveyBeritaAcaraPdfPreviewDialog } from './SurveyBeritaAcaraPdfPreviewDialog';
import { SurveyBeritaAcaraSignDialog } from './SurveyBeritaAcaraSignDialog';
import { SurveyRescheduleProposeDialog } from './SurveyRescheduleProposeDialog';
import { SurveyTambahTitikDialog } from './SurveyTambahTitikDialog';
import {
  tambahTitikOptionsFromSurveyRows,
  type SurveyTambahTitikOption,
} from './surveyTambahTitik';
import { surveyRowAirDisplay } from '../../SamplingWorkOrdersPage/samplingPlanAirGroup';

const BERITA_ACARA_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeBeritaAcaraEmails(values: string[]): { emails: string[]; invalid: string[] } {
  const emails: string[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    for (const part of String(value).split(/[,;\s]+/)) {
      const email = part.trim();
      if (!email) continue;
      if (!BERITA_ACARA_EMAIL_RE.test(email)) {
        if (!invalid.includes(email)) invalid.push(email);
        continue;
      }
      const key = email.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      emails.push(email);
    }
  }
  return { emails, invalid };
}

const STATUS_LABEL: Record<SurveyRowStatus, string> = {
  not_started: 'Belum mulai',
  in_progress: 'Berjalan',
  finished: 'Selesai sampling',
  customer_signed: 'TTD pelanggan',
  completed: 'Lengkap',
  cancelled: 'Dibatalkan',
};

type Props = {
  rows: StrapiSurveyGridRow[];
  loading?: boolean;
  /** Baris titik dari modal tambah titik yang belum punya berita acara. */
  highlightRowKeys?: Set<string>;
  workOrderDocumentId?: string;
  /** Bidang pengujian yang dapat ditambah titik (dari sampling plan kanonik WO). */
  titikPlanOptions?: SurveyTambahTitikOption[];
  /** Email customer quotation aktif. Kosong berarti input bebas. */
  defaultRecipientEmail?: string;
  /** Checklist kesiapan alat sudah disimpan. Selama false, mulai titik tetap disabled. */
  equipmentChecklistReady?: boolean;
};

export function SurveyBeritaAcaraGrid({
  rows: rowsProp,
  loading = false,
  highlightRowKeys,
  workOrderDocumentId: workOrderDocumentIdProp,
  titikPlanOptions,
  defaultRecipientEmail = '',
  equipmentChecklistReady = true,
}: Props) {
  const { jwt, user } = useAuth();
  const canSendBeritaAcaraPdf = isPetugasSampling(user);
  const qc = useQueryClient();

  const [startRow, setStartRow] = useState<StrapiSurveyGridRow | null>(null);
  const [finishRow, setFinishRow] = useState<StrapiSurveyGridRow | null>(null);
  const [cancelRow, setCancelRow] = useState<StrapiSurveyGridRow | null>(null);
  const [rescheduleRow, setRescheduleRow] = useState<StrapiSurveyGridRow | null>(null);
  const [rescheduleModalError, setRescheduleModalError] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelModalError, setCancelModalError] = useState<string | null>(null);
  const [signMode, setSignMode] = useState<'customer' | 'surveyor' | null>(null);
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [startLongitude, setStartLongitude] = useState('');
  const [startLatitude, setStartLatitude] = useState('');
  const [teamPresence, setTeamPresence] = useState<Record<string, TeamPresenceRowState>>({});
  const [gpsLoading, setGpsLoading] = useState(false);
  const [startModalError, setStartModalError] = useState<string | null>(null);
  const [rowPatches, setRowPatches] = useState<Record<string, SurveyRowPatch>>({});
  const [draggingRowKey, setDraggingRowKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [sendPdfOpen, setSendPdfOpen] = useState(false);
  const [sendPdfEmails, setSendPdfEmails] = useState<string[]>([]);
  const [sendPdfEmailInput, setSendPdfEmailInput] = useState('');
  const [sendPdfError, setSendPdfError] = useState<string | null>(null);
  const [sendPdfSuccess, setSendPdfSuccess] = useState<string | null>(null);
  const [tambahTitikOpen, setTambahTitikOpen] = useState(false);
  const [tambahTitikError, setTambahTitikError] = useState<string | null>(null);
  const [tambahTitikSuccess, setTambahTitikSuccess] = useState<string | null>(null);

  const startTeamsQ = useQuery({
    queryKey: ['strapi', 'surveyTeamsByWorkOrder', jwt, startRow?.work_order_document_id],
    queryFn: () => strapiListSurveyTeamsForWorkOrder(jwt!, startRow!.work_order_document_id),
    enabled: Boolean(jwt && startRow?.work_order_document_id),
  });
  const startTeams = startTeamsQ.data ?? [];

  const rescheduleTeamsQ = useQuery({
    queryKey: [
      'strapi',
      'surveyTeamsByWorkOrder',
      jwt,
      rescheduleRow?.work_order_document_id,
      'reschedule-blocked-dates',
    ],
    queryFn: () => strapiListSurveyTeamsForWorkOrder(jwt!, rescheduleRow!.work_order_document_id),
    enabled: Boolean(jwt && rescheduleRow?.work_order_document_id),
  });

  useEffect(() => {
    if (!startRow || startTeamsQ.isPending) return;
    if (!startTeams.length) {
      setTeamPresence({});
      return;
    }
    setTeamPresence(buildInitialTeamPresence(startTeams, startRow.survey_team_document_id));
  }, [startRow, startTeams, startTeamsQ.isPending]);

  const rows = useMemo(
    () => sortSurveyRowsByListOrder(mergeSurveyRowsWithPatches(rowsProp, rowPatches)),
    [rowsProp, rowPatches],
  );
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const rescheduleBlockedDates = useMemo(() => {
    const woId = rescheduleRow?.work_order_document_id?.trim() ?? '';
    if (!woId) return [];
    return collectBlockedRescheduleDates({
      workOrderDocumentId: woId,
      rows,
      teamScheduledDateFields: (rescheduleTeamsQ.data ?? []).map((team) => team.scheduled_date),
    });
  }, [rescheduleRow?.work_order_document_id, rows, rescheduleTeamsQ.data]);

  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: queryKeys.surveyRowsPrefix(jwt) });
  };

  const hasPresentMember = useMemo(
    () => Object.values(teamPresence).some((r) => r.isPresence),
    [teamPresence],
  );

  async function handleCaptureGps() {
    setGpsLoading(true);
    setStartModalError(null);
    try {
      const geo = await readCurrentPosition();
      setStartLongitude(String(geo.longitude));
      setStartLatitude(String(geo.latitude));
    } catch (e) {
      setStartModalError(e instanceof Error ? e.message : 'Gagal mengambil GPS.');
    } finally {
      setGpsLoading(false);
    }
  }

  const startM = useMutation({
    mutationFn: async () => {
      if (!jwt || !startRow) throw new Error('Sesi tidak valid.');
      const geo = resolveSurveyStartCoordinates(startLongitude, startLatitude);
      const presences: StrapiStartSurveyPresenceRow[] = [];
      for (const team of startTeams) {
        const teamDocId = String(team.documentId ?? '').trim();
        if (!teamDocId) continue;
        const st = teamPresence[teamDocId] ?? { isPresence: false, pictureFile: null };
        let picture: string | undefined;
        if (st.isPresence && st.pictureFile) {
          picture = await readFileAsDataUrl(st.pictureFile);
        }
        presences.push({
          survey_team_document_id: teamDocId,
          is_presence: st.isPresence,
          ...(picture ? { picture } : {}),
        });
      }
      return strapiStartSurvey(jwt, {
        sampling_plan_document_id: startRow.sampling_plan_document_id,
        survey_team_document_id: startRow.survey_team_document_id,
        point_index: startRow.point_index,
        ...(geo ? { longitude: geo.longitude, latitude: geo.latitude } : {}),
        presences,
      });
    },
    onSuccess: async ({ letter, surveyRowPatch }) => {
      const woDocId = startRow?.work_order_document_id?.trim() ?? '';
      const rowKey = surveyRowPatch?.row_key ?? startRow?.row_key;
      const letterDocId = String(
        surveyRowPatch?.berita_acara_document_id ?? letter.documentId ?? '',
      ).trim();
      const startTime =
        surveyRowPatch?.start_time ??
        (letter.start_time ? String(letter.start_time) : new Date().toISOString());
      if (rowKey && letterDocId) {
        const rowPatch: SurveyRowPatch = {
          berita_acara_document_id: letterDocId,
          status: 'in_progress',
          start_time: startTime,
          list_order: surveyRowPatch?.list_order ?? null,
        };
        setRowPatches((prev) => ({ ...prev, [rowKey]: rowPatch }));
      }
      setStartRow(null);
      setStartLongitude('');
      setStartLatitude('');
      setTeamPresence({});
      setStartModalError(null);
      setActionError(null);
      await invalidate();
      await qc.invalidateQueries({ queryKey: queryKeys.workOrdersListPrefix(jwt) });
      if (woDocId) {
        await qc.invalidateQueries({ queryKey: queryKeys.workOrderDetail(jwt, woDocId) });
      }
    },
    onError: (e) =>
      setStartModalError(e instanceof Error ? e.message : 'Gagal memulai sampling.'),
  });

  const cancelM = useMutation({
    mutationFn: async () => {
      if (!jwt || !cancelRow) throw new Error('Sesi tidak valid.');
      const reason = cancelReason.trim();
      if (!reason) throw new Error('Alasan pembatalan wajib diisi.');
      return strapiCancelSurveyPoint(jwt, {
        sampling_plan_document_id: cancelRow.sampling_plan_document_id,
        survey_team_document_id: cancelRow.survey_team_document_id,
        point_index: cancelRow.point_index,
        cancel_reason: reason,
      });
    },
    onSuccess: async ({ letter, surveyRowPatch }) => {
      const woDocId = cancelRow?.work_order_document_id?.trim() ?? '';
      const rowKey = surveyRowPatch?.row_key ?? cancelRow?.row_key;
      const letterDocId = String(
        surveyRowPatch?.berita_acara_document_id ?? letter.documentId ?? '',
      ).trim();
      if (rowKey && letterDocId) {
        const rowPatch: SurveyRowPatch = {
          berita_acara_document_id: letterDocId,
          status: 'cancelled',
          list_order: null,
          cancelled_at: surveyRowPatch?.cancelled_at ?? new Date().toISOString(),
          cancel_reason: surveyRowPatch?.cancel_reason ?? cancelReason.trim(),
        };
        setRowPatches((prev) => ({ ...prev, [rowKey]: rowPatch }));
      }
      setCancelRow(null);
      setCancelReason('');
      setCancelModalError(null);
      setActionError(null);
      await invalidate();
      if (woDocId) {
        await qc.invalidateQueries({ queryKey: queryKeys.workOrderDetail(jwt, woDocId) });
      }
    },
    onError: (e) =>
      setCancelModalError(e instanceof Error ? e.message : 'Gagal membatalkan titik.'),
  });

  const rescheduleM = useMutation({
    mutationFn: async (payload: { proposedDate: string; reason: string }) => {
      if (!jwt || !rescheduleRow) throw new Error('Sesi tidak valid.');
      return strapiRescheduleProposeSurveyPoint(jwt, {
        sampling_plan_document_id: rescheduleRow.sampling_plan_document_id,
        survey_team_document_id: rescheduleRow.survey_team_document_id,
        point_index: rescheduleRow.point_index,
        proposed_date: payload.proposedDate,
        ...(payload.reason ? { reason: payload.reason } : {}),
      });
    },
    onSuccess: async ({ surveyRowPatch }) => {
      const woDocId = rescheduleRow?.work_order_document_id?.trim() ?? '';
      const rowKey = surveyRowPatch?.row_key ?? rescheduleRow?.row_key;
      if (rowKey && surveyRowPatch) {
        setRowPatches((prev) => ({
          ...prev,
          [rowKey]: {
            ...prev[rowKey],
            reschedule_status: surveyRowPatch.reschedule_status,
            reschedule_date: surveyRowPatch.reschedule_date,
            reschedule_schedule_document_id: surveyRowPatch.reschedule_schedule_document_id,
            scheduled_date: surveyRowPatch.scheduled_date,
          },
        }));
      }
      setRescheduleRow(null);
      setRescheduleModalError(null);
      setActionError(null);
      await invalidate();
      if (woDocId) {
        await qc.invalidateQueries({ queryKey: queryKeys.workOrderDetail(jwt, woDocId) });
      }
    },
    onError: (e) =>
      setRescheduleModalError(e instanceof Error ? e.message : 'Gagal mengajukan reschedule.'),
  });

  const signM = useMutation({
    mutationFn: async (sign: string) => {
      if (!jwt || !signMode) throw new Error('Mode tanda tangan tidak valid.');
      const trimmed = sign.trim();
      if (!trimmed) throw new Error('Tanda tangan wajib.');
      const mode = signMode;
      const targets = selectedSignRows(rows, selection, mode);
      const ids = targets
        .map((row) => row.berita_acara_document_id)
        .filter((id): id is string => Boolean(id));
      if (!ids.length) {
        throw new Error(
          mode === 'customer'
            ? 'Pilih baris berstatus selesai sampling yang belum ditandatangani pelanggan.'
            : 'Pilih baris yang sudah selesai sampling untuk tanda tangan petugas.',
        );
      }
      await strapiBulkSignBeritaAcara(jwt, mode, ids, trimmed);
      return { mode, sign: trimmed, rowKeys: targets.map((row) => row.row_key) };
    },
    onSuccess: async ({ mode, sign, rowKeys }) => {
      setRowPatches((prev) => {
        const next = { ...prev };
        for (const rowKey of rowKeys) {
          const base = rowsProp.find((r) => r.row_key === rowKey);
          const merged = base ? applySurveyRowPatch(base, prev[rowKey]) : null;
          if (!merged) continue;
          next[rowKey] = { ...prev[rowKey], ...patchSurveyRowAfterSign(merged, mode, sign) };
        }
        return next;
      });
      setSignMode(null);
      setSelection([]);
      setActionError(null);
      await invalidate();
    },
    onError: (e) => setActionError(e instanceof Error ? e.message : 'Gagal menyimpan tanda tangan.'),
  });

  const signTargetRows = useMemo(
    () => (signMode ? selectedSignRows(rows, selection, signMode) : []),
    [rows, selection, signMode],
  );

  const resolvedTitikOptions = useMemo(
    () => (titikPlanOptions?.length ? titikPlanOptions : tambahTitikOptionsFromSurveyRows(rows)),
    [titikPlanOptions, rows],
  );

  const tambahTitikM = useMutation({
    mutationFn: async (payload: { samplingPlanDocumentId: string; addedPointCount: number }) => {
      if (!jwt) throw new Error('Sesi tidak valid.');
      const woDocId =
        workOrderDocumentIdProp?.trim() ||
        rows.find((r) => r.work_order_document_id)?.work_order_document_id?.trim() ||
        '';
      if (!woDocId) throw new Error('Work order tidak ditemukan pada baris sampling.');
      return strapiAddSamplingPointsForWorkOrder(jwt, {
        workOrderDocumentId: woDocId,
        samplingPlanDocumentId: payload.samplingPlanDocumentId,
        addedPointCount: payload.addedPointCount,
      });
    },
    onSuccess: async (result) => {
      setTambahTitikOpen(false);
      setTambahTitikError(null);
      setActionError(null);
      setTambahTitikSuccess(
        `${result.bidang_pengujian_label || 'Bidang pengujian'}: ${result.previous_point_count} → ${result.point_count} titik. Quotation masuk permintaan revisi.`,
      );
      const woDocId =
        result.work_order_document_id ||
        workOrderDocumentIdProp?.trim() ||
        rows.find((r) => r.work_order_document_id)?.work_order_document_id?.trim() ||
        '';
      await invalidate();
      await qc.invalidateQueries({ queryKey: queryKeys.workOrdersListPrefix(jwt) });
      if (woDocId) {
        await qc.invalidateQueries({ queryKey: queryKeys.workOrderDetail(jwt, woDocId) });
        await qc.invalidateQueries({ queryKey: queryKeys.surveyRowsForWorkOrder(jwt, woDocId) });
      }
      await qc.invalidateQueries({
        queryKey: ['strapi', 'samplingPlansByWorkOrder', jwt, woDocId],
      });
      await qc.invalidateQueries({
        queryKey: ['strapi', 'quotationSampleSyncPreview', jwt, woDocId],
      });
      if (result.quotation_document_id) {
        await qc.invalidateQueries({
          queryKey: queryKeys.quotationDetail(jwt, result.quotation_document_id),
        });
        await qc.invalidateQueries({ queryKey: queryKeys.quotationsListPrefix(jwt) });
      }
    },
    onError: (e) =>
      setTambahTitikError(e instanceof Error ? e.message : 'Gagal menambah titik sampling.'),
  });

  const reorderM = useMutation({
    mutationFn: async (orderedDocumentIds: string[]) => {
      if (!jwt) throw new Error('Sesi tidak valid.');
      const woDocId =
        workOrderDocumentIdProp?.trim() ||
        rowsRef.current.find((row) => row.work_order_document_id)?.work_order_document_id ||
        '';
      await strapiReorderBeritaAcaraLetters(jwt, woDocId, orderedDocumentIds);
    },
    onError: (e) => {
      setActionError(e instanceof Error ? e.message : 'Gagal mengatur urutan berita acara.');
      void invalidate();
    },
    onSuccess: async () => {
      await invalidate();
    },
  });

  const applyReorder = useCallback(
    (fromKey: string, toKey: string) => {
      const result = reorderSurveyLetterRows(rowsRef.current, fromKey, toKey);
      if (!result) return;
      setRowPatches((prev) => {
        const next = { ...prev };
        for (const row of result.rows) {
          if (!row.berita_acara_document_id || row.list_order == null) continue;
          next[row.row_key] = { ...next[row.row_key], list_order: row.list_order };
        }
        return next;
      });
      reorderM.mutate(result.orderedDocumentIds);
    },
    [reorderM],
  );

  const columns: GridColDef<StrapiSurveyGridRow>[] = useMemo(
    () => [
      {
        field: 'kode_sampel',
        headerName: 'Kode Sampel',
        width: 176,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const kode = surveyRowKodeSampel(row) || '—';
          const draggable = canDragSurveyLetterRow(row);
          return (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ width: '100%', minWidth: 0 }}
              onDragOver={(e) => {
                if (!draggingRowKey) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDrop={(e) => {
                e.preventDefault();
                const fromKey = e.dataTransfer.getData('text/plain') || draggingRowKey || '';
                applyReorder(fromKey, row.row_key);
                setDraggingRowKey(null);
              }}
            >
              {draggable ? (
                <Box
                  component="span"
                  draggable
                  onPointerDown={(e) => e.stopPropagation()}
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', row.row_key);
                    e.dataTransfer.effectAllowed = 'move';
                    setDraggingRowKey(row.row_key);
                  }}
                  onDragEnd={() => setDraggingRowKey(null)}
                  sx={{
                    display: 'inline-flex',
                    cursor: draggingRowKey === row.row_key ? 'grabbing' : 'grab',
                    color: 'text.secondary',
                    flexShrink: 0,
                  }}
                  aria-label={`Geser urutan ${kode}`}
                  data-testid={`survey-berita-acara-drag-${String(row.row_key).replace(/:/g, '__')}`}
                >
                  <DragIndicatorIcon fontSize="small" />
                </Box>
              ) : (
                <Box sx={{ width: 20, flexShrink: 0 }} />
              )}
              <Typography variant="body2" noWrap title={kode === '—' ? undefined : kode}>
                {kode}
              </Typography>
            </Stack>
          );
        },
      },
      { field: 'bidang_pengujian_label', headerName: 'Bidang pengujian', minWidth: 140, flex: 1 },
      { field: 'point_label', headerName: 'Titik', width: 90 },
      {
        field: 'is_air',
        headerName: 'Air',
        width: 72,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => surveyRowAirDisplay(row.is_air),
      },
      {
        field: 'survey_user_label',
        headerName: 'Petugas',
        minWidth: 120,
        flex: 0.8,
        valueGetter: (_, row) =>
          row.survey_user_label ||
          (row.survey_user_id != null ? `User #${row.survey_user_id}` : '—'),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 180,
        renderCell: ({ row }) => {
          const label = STATUS_LABEL[row.status] ?? row.status;
          const reason = (row.cancel_reason ?? '').trim();
          let rescheduleNote = '';
          if (row.reschedule_status === 'pending_staff') {
            rescheduleNote = row.reschedule_date
              ? `Reschedule: menunggu admin (${row.reschedule_date})`
              : 'Reschedule: menunggu admin';
          } else if (row.reschedule_status === 'ready') {
            rescheduleNote = row.reschedule_date
              ? `Reschedule siap (${row.reschedule_date})`
              : 'Reschedule siap';
          }
          if (row.status === 'cancelled' && reason) {
            return (
              <Tooltip title={reason}>
                <span>{label}</span>
              </Tooltip>
            );
          }
          if (rescheduleNote) {
            return (
              <Tooltip title={rescheduleNote}>
                <span>
                  {label}
                  <Typography component="span" variant="caption" display="block" color="warning.main">
                    {rescheduleNote}
                  </Typography>
                </span>
              </Tooltip>
            );
          }
          return label;
        },
      },
      {
        field: 'scheduled_date',
        headerName: 'Jadwal',
        width: 120,
        valueGetter: (_, row) => row.scheduled_date || row.reschedule_date || '—',
      },
      {
        field: 'start_time',
        headerName: 'Mulai',
        width: 150,
        valueGetter: (_, row) =>
          row.start_time ? new Date(row.start_time).toLocaleString('id-ID') : '—',
      },
      {
        field: 'actions',
        headerName: 'Aksi',
        width: 268,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Stack
            direction="row"
            spacing={0.25}
            sx={{ py: 0.5 }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {canFinishSurveyRow(row) ? (
              <Tooltip title="Selesai sampling">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    aria-label={`Selesai sampling ${row.point_label}`}
                    data-testid={`survey-berita-acara-finish-${String(row.row_key).replace(/:/g, '__')}`}
                    onClick={() => {
                      setActionError(null);
                      setFinishRow(row);
                    }}
                  >
                    <TaskAltOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : canEditSurveySamplingRow(row) ? (
              <Tooltip title="Edit form sampling">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    aria-label={`Edit form sampling ${row.point_label}`}
                    data-testid={`survey-berita-acara-edit-${String(row.row_key).replace(/:/g, '__')}`}
                    onClick={() => {
                      setActionError(null);
                      setFinishRow(row);
                    }}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : !String(row.job_number ?? '').trim() ? (
              <Tooltip title="Menunggu persetujuan admin sampling">
                <span>
                  <IconButton
                    size="small"
                    disabled
                    aria-label={`Menunggu nomor job ${row.point_label}`}
                  >
                    <PlayArrowOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : canStartSurveyRow(row) && !equipmentChecklistReady ? (
              <Tooltip title="Lengkapi checklist kesiapan alat terlebih dahulu">
                <span>
                  <IconButton
                    size="small"
                    disabled
                    aria-label={`Checklist kesiapan alat belum disimpan ${row.point_label}`}
                  >
                    <PlayArrowOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : canStartSurveyRow(row) ? (
              <Tooltip title="Mulai titik">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    aria-label={`Mulai titik ${row.point_label}`}
                    data-testid={`survey-berita-acara-start-${String(row.row_key).replace(/:/g, '__')}`}
                    disabled={startM.isPending || cancelM.isPending || rescheduleM.isPending}
                    onClick={() => {
                      setActionError(null);
                      setStartModalError(null);
                      setStartLongitude('');
                      setStartLatitude('');
                      setTeamPresence({});
                      setStartRow(row);
                    }}
                  >
                    <PlayArrowOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : row.reschedule_status === 'pending_staff' ? (
              <Tooltip title="Menunggu konfirmasi admin sampling">
                <span>
                  <IconButton size="small" disabled aria-label={`Menunggu admin ${row.point_label}`}>
                    <PlayArrowOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            {canRescheduleSurveyRow(row) ? (
              <Tooltip title="Reschedule titik">
                <span>
                  <IconButton
                    size="small"
                    color="warning"
                    aria-label={`Reschedule titik ${row.point_label}`}
                    data-testid={`survey-berita-acara-reschedule-${String(row.row_key).replace(/:/g, '__')}`}
                    disabled={rescheduleM.isPending || startM.isPending || cancelM.isPending}
                    onClick={() => {
                      setActionError(null);
                      setRescheduleModalError(null);
                      setRescheduleRow(row);
                    }}
                  >
                    <EventRepeatOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            {canCancelSurveyRow(row) ? (
              <Tooltip title="Batalkan titik">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={`Batalkan titik ${row.point_label}`}
                    data-testid={`survey-berita-acara-cancel-${String(row.row_key).replace(/:/g, '__')}`}
                    disabled={cancelM.isPending || startM.isPending || rescheduleM.isPending}
                    onClick={() => {
                      setActionError(null);
                      setCancelModalError(null);
                      setCancelReason('');
                      setCancelRow(row);
                    }}
                  >
                    <CancelOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            <Tooltip title="Tanda tangan pelanggan">
              <span>
                <IconButton
                  size="small"
                  aria-label={`Tanda tangan pelanggan ${row.point_label}`}
                  disabled={
                    row.status !== 'finished' || signM.isPending || !row.berita_acara_document_id
                  }
                  onClick={() => {
                    setActionError(null);
                    setSelection([row.row_key]);
                    setSignMode('customer');
                  }}
                >
                  <BorderColorOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Tanda tangan petugas">
              <span>
                <IconButton
                  size="small"
                  aria-label={`Tanda tangan petugas ${row.point_label}`}
                  disabled={
                    (row.status !== 'finished' && row.status !== 'customer_signed') ||
                    signM.isPending ||
                    !row.berita_acara_document_id ||
                    Boolean(String(row.surveyor_sign ?? '').trim())
                  }
                  onClick={() => {
                    setActionError(null);
                    setSelection([row.row_key]);
                    setSignMode('surveyor');
                  }}
                >
                  <HowToRegOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {surveyRowIsBeritaAcaraPrintReady(row) ? (
              <Tooltip title="PDF berita acara sudah tersedia">
                <Box
                  component="span"
                  aria-label={`PDF berita acara sudah tersedia untuk ${row.point_label}`}
                  data-testid={`survey-berita-acara-pdf-ready-${String(row.row_key).replace(/:/g, '__')}`}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: 'error.main',
                    px: 0.5,
                    cursor: 'default',
                  }}
                >
                  <PictureAsPdfOutlinedIcon fontSize="small" />
                </Box>
              </Tooltip>
            ) : null}
          </Stack>
        ),
      },
    ],
    [
      applyReorder,
      draggingRowKey,
      signM.isPending,
      startM.isPending,
      cancelM.isPending,
      rescheduleM.isPending,
    ],
  );

  const applySendPdfEmails = useCallback((values: string[]) => {
    const { emails, invalid } = normalizeBeritaAcaraEmails(values);
    setSendPdfEmails(emails);
    setSendPdfError(invalid.length ? `Alamat email tidak valid: ${invalid.join(', ')}` : null);
    return { emails, invalid };
  }, []);

  const sendPdfM = useMutation({
    mutationFn: async (emails: string[]) => {
      if (!jwt) throw new Error('Sesi login tidak ada.');
      const woId = (
        rowsRef.current.find((r) => r.work_order_document_id)?.work_order_document_id ||
        workOrderDocumentIdProp ||
        ''
      ).trim();
      if (!woId) throw new Error('Work order tidak ditemukan.');
      const { emails: recipients, invalid } = normalizeBeritaAcaraEmails(emails);
      if (!recipients.length || invalid.length) throw new Error('Alamat email tidak valid.');
      await strapiSendWorkOrderBeritaAcaraPdf(jwt, woId, recipients);
      return recipients;
    },
    onSuccess: (recipients) => {
      setSendPdfOpen(false);
      setSendPdfEmailInput('');
      setSendPdfError(null);
      setSendPdfSuccess(`PDF berita acara terkirim ke ${recipients.join(', ')}.`);
    },
    onError: (error) => {
      setSendPdfError(error instanceof Error ? error.message : 'Gagal mengirim PDF berita acara.');
    },
  });

  const gridRows = useMemo(() => rows.map((r) => ({ ...r, id: r.row_key })), [rows]);
  const beritaAcaraCount = rows.filter((r) => r.berita_acara_document_id).length;
  const printReadyCount = rows.filter(surveyRowIsBeritaAcaraPrintReady).length;
  const sendPdfDraft = normalizeBeritaAcaraEmails([...sendPdfEmails, sendPdfEmailInput]);
  const workOrderDocumentId = rows.find((r) => r.work_order_document_id)?.work_order_document_id ?? '';
  const workOrderLabel = rows.find((r) => r.work_order_label)?.work_order_label ?? '';

  return (
    <Stack spacing={1} data-testid="survey-berita-acara-grid">
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Berita acara
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {beritaAcaraCount} berita acara dari {rows.length} titik sampling
        </Typography>
        <Box sx={{ flex: 1, minWidth: 8 }} />
        {rows.length === 0 && resolvedTitikOptions.length > 0 ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddLocationAltOutlinedIcon />}
            disabled={tambahTitikM.isPending}
            onClick={() => {
              setActionError(null);
              setTambahTitikError(null);
              setTambahTitikOpen(true);
            }}
            data-testid="survey-tambah-titik"
          >
            Tambah Titik
          </Button>
        ) : null}
        {rows.length > 0 ? (
          <>
            {printReadyCount > 0 ? (
              <Button
                variant="contained"
                size="small"
                startIcon={<PrintOutlinedIcon />}
                onClick={() => {
                  setActionError(null);
                  setPdfPreviewOpen(true);
                }}
                data-testid="survey-berita-acara-print"
              >
                Print Berita acara
              </Button>
            ) : null}
            {canSendBeritaAcaraPdf && printReadyCount > 0 ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={<ForwardToInboxOutlinedIcon />}
                disabled={sendPdfM.isPending}
                onClick={() => {
                  setActionError(null);
                  setSendPdfError(null);
                  setSendPdfEmailInput('');
                  const preset = defaultRecipientEmail.trim();
                  setSendPdfEmails(BERITA_ACARA_EMAIL_RE.test(preset) ? [preset] : []);
                  setSendPdfOpen(true);
                }}
                data-testid="survey-berita-acara-send-pdf"
              >
                Kirim PDF berita acara
              </Button>
            ) : null}
            <Button
              variant="outlined"
              size="small"
              disabled={!selection.length || signM.isPending}
              onClick={() => {
                setActionError(null);
                setSignMode('customer');
              }}
              data-testid="survey-berita-acara-bulk-customer-sign"
            >
              TTD pelanggan (bulk)
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={!selection.length || signM.isPending}
              onClick={() => {
                setActionError(null);
                setSignMode('surveyor');
              }}
              data-testid="survey-berita-acara-bulk-surveyor-sign"
            >
              TTD petugas (bulk)
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddLocationAltOutlinedIcon />}
              disabled={!resolvedTitikOptions.length || tambahTitikM.isPending}
              onClick={() => {
                setActionError(null);
                setTambahTitikError(null);
                setTambahTitikOpen(true);
              }}
              data-testid="survey-tambah-titik"
            >
              Tambah Titik
            </Button>
            {selection.length > 0 ? (
              <Typography variant="caption" color="text.secondary">
                {selection.length} baris dipilih
              </Typography>
            ) : null}
          </>
        ) : null}
      </Stack>

      {actionError ? (
        <Alert severity="error" onClose={() => setActionError(null)} sx={{ flexShrink: 0 }}>
          {actionError}
        </Alert>
      ) : null}
      {sendPdfSuccess ? (
        <Alert severity="success" onClose={() => setSendPdfSuccess(null)} sx={{ flexShrink: 0 }}>
          {sendPdfSuccess}
        </Alert>
      ) : null}
      {tambahTitikSuccess ? (
        <Alert severity="success" onClose={() => setTambahTitikSuccess(null)}>
          {tambahTitikSuccess}
        </Alert>
      ) : null}

      {rows.length === 0 ? (
        <Alert severity="info">
          Belum ada titik sampling. Pastikan rencana sampling sudah disetujui dan tim survey sudah
          terdaftar.
        </Alert>
      ) : (
        <DataGrid
          rows={gridRows}
          columns={columns}
          loading={loading}
          checkboxSelection
          disableRowSelectionOnClick
          disableColumnSorting
          rowSelectionModel={selection}
          onRowSelectionModelChange={setSelection}
          isRowSelectable={(params) =>
            Boolean(params.row.berita_acara_document_id) &&
            (params.row.status === 'finished' || params.row.status === 'customer_signed')
          }
          getRowId={(r) => r.row_key}
          getRowClassName={(params) => {
            const pending = highlightRowKeys?.has(String(params.row.row_key ?? ''))
              ? 'survey-row-pending-plan'
              : '';
            const dragging =
              draggingRowKey && params.row.row_key === draggingRowKey ? 'survey-row-dragging' : '';
            return [pending, dragging].filter(Boolean).join(' ');
          }}
          pageSizeOptions={DATA_GRID_NUMBERED_PAGE_SIZE_OPTIONS}
          initialState={{ pagination: { paginationModel: { pageSize: DATA_GRID_DEFAULT_PAGE_SIZE, page: 0 } } }}
          slots={{ pagination: DataGridNumberedPagination }}
          slotProps={{
            row: {
              onDragOver: (event) => {
                if (!draggingRowKey) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = 'move';
              },
              onDrop: (event) => {
                event.preventDefault();
                const toKey = String(event.currentTarget.getAttribute('data-id') ?? '');
                const fromKey = event.dataTransfer.getData('text/plain') || draggingRowKey || '';
                applyReorder(fromKey, toKey);
                setDraggingRowKey(null);
              },
            },
          }}
          autoHeight
          sx={{
            minHeight: 200,
            '& .survey-row-pending-plan': {
              bgcolor: 'warning.light',
              '&:hover': { bgcolor: 'warning.light' },
            },
            '& .survey-row-dragging': { opacity: 0.55 },
          }}
        />
      )}

      <Dialog
        open={Boolean(startRow)}
        onClose={() => {
          if (startM.isPending) return;
          setStartModalError(null);
          setStartRow(null);
        }}
        maxWidth="md"
        fullWidth
        data-testid="survey-berita-acara-start-dialog"
      >
        <DialogTitle>Mulai sampling — titik</DialogTitle>
        <DialogContent>
          {startModalError ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStartModalError(null)}>
              {startModalError}
            </Alert>
          ) : null}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {startRow?.bidang_pengujian_label} — {startRow?.point_label}
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Tim sampling — kehadiran
          </Typography>
          {startTeamsQ.isPending ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : startTeams.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Belum ada tim sampling pada work order ini.
            </Alert>
          ) : (
            <StartSurveyTeamPresenceByDateTabs
              teams={startTeams}
              teamPresence={teamPresence}
              onTeamPresenceChange={setTeamPresence}
              presenceTestIdPrefix="survey-berita-acara-start-presence"
            />
          )}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={gpsLoading || startM.isPending}
              onClick={() => void handleCaptureGps()}
            >
              {gpsLoading ? 'Mengambil GPS…' : 'Ambil GPS'}
            </Button>
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField
              label="Longitude (opsional)"
              size="small"
              fullWidth
              value={startLongitude}
              onChange={(e) => setStartLongitude(e.target.value)}
            />
            <TextField
              label="Latitude (opsional)"
              size="small"
              fullWidth
              value={startLatitude}
              onChange={(e) => setStartLatitude(e.target.value)}
            />
          </Stack>
          <SurveyGpsHemisphereResult longitudeText={startLongitude} latitudeText={startLatitude} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartRow(null)} disabled={startM.isPending}>
            Batal
          </Button>
          <Button
            variant="contained"
            data-testid="survey-berita-acara-start-submit"
            onClick={() => startM.mutate()}
            disabled={
              startM.isPending ||
              gpsLoading ||
              startTeamsQ.isPending ||
              !hasPresentMember ||
              startTeams.length === 0
            }
          >
            {startM.isPending ? 'Memproses…' : 'Mulai'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(cancelRow)}
        onClose={() => {
          if (cancelM.isPending) return;
          setCancelRow(null);
          setCancelReason('');
          setCancelModalError(null);
        }}
        maxWidth="sm"
        fullWidth
        data-testid="survey-berita-acara-cancel-dialog"
      >
        <DialogTitle>Batalkan titik — {cancelRow?.point_label ?? ''}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Titik yang dibatalkan tidak dikerjakan dan tidak dapat di-undo. Jumlah titik pada rencana
            tetap sama.
          </Alert>
          {cancelRow ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {cancelRow.bidang_pengujian_label} ·{' '}
              {cancelRow.work_order_label || cancelRow.work_order_document_id}
            </Typography>
          ) : null}
          <TextField
            label="Alasan pembatalan"
            fullWidth
            required
            multiline
            minRows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Contoh: permintaan pelanggan, akses lokasi tidak memungkinkan, dll."
            data-testid="survey-berita-acara-cancel-reason"
          />
          {cancelModalError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {cancelModalError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (cancelM.isPending) return;
              setCancelRow(null);
              setCancelReason('');
              setCancelModalError(null);
            }}
            disabled={cancelM.isPending}
          >
            Tutup
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={cancelM.isPending || !cancelReason.trim()}
            onClick={() => cancelM.mutate()}
            data-testid="survey-berita-acara-cancel-submit"
          >
            {cancelM.isPending ? 'Menyimpan…' : 'Batalkan titik'}
          </Button>
        </DialogActions>
      </Dialog>

      <SurveyRescheduleProposeDialog
        open={Boolean(rescheduleRow)}
        pointLabel={rescheduleRow?.point_label ?? ''}
        submitting={rescheduleM.isPending || rescheduleTeamsQ.isFetching}
        error={rescheduleModalError}
        blockedDates={rescheduleBlockedDates}
        onClose={() => {
          if (rescheduleM.isPending) return;
          setRescheduleRow(null);
          setRescheduleModalError(null);
        }}
        onSubmit={(payload) => rescheduleM.mutate(payload)}
      />

      <SurveyBeritaAcaraFinishDialog
        finishRow={finishRow}
        onClose={() => setFinishRow(null)}
        onSuccess={async ({ endTime, rowKey, completed }) => {
          if (completed && endTime) {
            const rowPatch: SurveyRowPatch = { status: 'finished', end_time: endTime };
            setRowPatches((prev) => ({ ...prev, [rowKey]: { ...prev[rowKey], ...rowPatch } }));
          }
          setFinishRow(null);
          setActionError(null);
          await invalidate();
        }}
      />

      <SurveyBeritaAcaraSignDialog
        open={Boolean(signMode)}
        mode={signMode}
        targetRows={signTargetRows}
        selectedCount={selection.length}
        submitting={signM.isPending}
        onClose={() => setSignMode(null)}
        onSubmit={(sign) => signM.mutate(sign)}
      />

      <Dialog
        open={sendPdfOpen}
        onClose={() => {
          if (sendPdfM.isPending) return;
          setSendPdfOpen(false);
          setSendPdfEmailInput('');
          setSendPdfError(null);
        }}
        maxWidth="sm"
        fullWidth
        data-testid="survey-berita-acara-send-pdf-dialog"
      >
        <DialogTitle>Kirim PDF berita acara</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            PDF berisi berita acara yang sudah ditandatangani petugas dan pelanggan
            {printReadyCount > 0 ? ` (${printReadyCount} titik)` : ''}.
          </Typography>
          <Autocomplete<string, true, false, true>
            multiple
            freeSolo
            autoSelect
            open={false}
            forcePopupIcon={false}
            options={[]}
            value={sendPdfEmails}
            inputValue={sendPdfEmailInput}
            onInputChange={(_, next, reason) => {
              if (reason !== 'input') return;
              if (!/[,;\s]/.test(next)) {
                setSendPdfEmailInput(next);
                return;
              }
              const endsWithSeparator = /[,;\s]$/.test(next);
              const parts = next.split(/[,;\s]+/).map((part) => part.trim()).filter(Boolean);
              const rest = endsWithSeparator ? '' : (parts.pop() ?? '');
              const committed = rest && BERITA_ACARA_EMAIL_RE.test(rest) ? [...parts, rest] : parts;
              applySendPdfEmails([...sendPdfEmails, ...committed]);
              setSendPdfEmailInput(rest && !BERITA_ACARA_EMAIL_RE.test(rest) ? rest : '');
            }}
            onChange={(_, next) => {
              applySendPdfEmails(next.map(String));
              setSendPdfEmailInput('');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Email"
                fullWidth
                required
                autoFocus
                placeholder={sendPdfEmails.length ? '' : 'nama@perusahaan.com'}
                helperText={
                  defaultRecipientEmail.trim()
                    ? 'Diisi dari email customer quotation yang aktif. Ketik email lain lalu tekan Enter, koma, atau spasi.'
                    : 'Customer quotation tidak punya email. Ketik alamat lalu tekan Enter, koma, atau spasi.'
                }
                data-testid="survey-berita-acara-send-pdf-email"
                inputProps={{
                  ...params.inputProps,
                  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                    const draft = event.currentTarget.value.trim();
                    if ((event.key === 'Enter' || event.key === ',' || event.key === ' ') && draft) {
                      event.preventDefault();
                      event.stopPropagation();
                      applySendPdfEmails([...sendPdfEmails, draft]);
                      setSendPdfEmailInput('');
                      return;
                    }
                    params.inputProps.onKeyDown?.(event);
                  },
                }}
              />
            )}
          />
          {sendPdfError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {sendPdfError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (sendPdfM.isPending) return;
              setSendPdfOpen(false);
              setSendPdfEmailInput('');
              setSendPdfError(null);
            }}
            disabled={sendPdfM.isPending}
          >
            Batal
          </Button>
          <Button
            variant="contained"
            disabled={sendPdfM.isPending || sendPdfDraft.emails.length === 0}
            onClick={() => {
              const { emails, invalid } = applySendPdfEmails([...sendPdfEmails, sendPdfEmailInput]);
              if (invalid.length || !emails.length) return;
              setSendPdfEmailInput('');
              sendPdfM.mutate(emails);
            }}
            data-testid="survey-berita-acara-send-pdf-submit"
          >
            {sendPdfM.isPending ? 'Mengirim…' : 'Kirim'}
          </Button>
        </DialogActions>
      </Dialog>

      <SurveyBeritaAcaraPdfPreviewDialog
        open={pdfPreviewOpen}
        workOrderDocumentId={workOrderDocumentId || null}
        workOrderLabel={workOrderLabel}
        jwt={jwt}
        onClose={() => setPdfPreviewOpen(false)}
      />

      <SurveyTambahTitikDialog
        open={tambahTitikOpen}
        options={resolvedTitikOptions}
        submitting={tambahTitikM.isPending}
        error={tambahTitikError}
        onClose={() => {
          if (tambahTitikM.isPending) return;
          setTambahTitikOpen(false);
          setTambahTitikError(null);
        }}
        onSubmit={(payload) => tambahTitikM.mutate(payload)}
      />
    </Stack>
  );
}
